export const project = 'stellantis'; // need this for when we dynamically import utils

// The ws-tagging-stellantis-adobe-updates LaunchDarkly flag, which gates the
// whole WSP 2.0 rollout. This is the ONLY place the flag key is spelled out in
// src/ — every mapping rule, page-alias override and util reads it through this
// helper, so the key cannot drift and a `grep isAdobeUpdatesOn` finds every
// gated behavior. It lives here rather than in the mapping because
// `sending` below needs it too, and the page-alias overrides import from here
// already.
//
// Always returns a real boolean: several call sites use it inside an object
// spread (`...(isAdobeUpdatesOn({ flags }) && { ... })`), where a raw undefined
// would still work but reads as an accident.
export const isAdobeUpdatesOn = ({ flags } = {}) =>
	Boolean(flags?.['ws-tagging-stellantis-adobe-updates']);

const DEFAULT_TYPE = 'trigger';

// SDG §5.4.1 component `type`, keyed on the widget that fired the event
// (US1906349). Stellantis agreed to base this on widget rather than page
// section. The first twelve rows are the client's table verbatim;
// `ws-inv-listing` comes from US2046693, which already emits
// `vehicle-inventory` for SRP card CTAs and is the only widget in the codebase
// whose type is derivable from widgetName alone and not already listed.
//
// Widgets absent here get no `type` on purpose. The nav, footer, sidebar and
// click-to-call rules derive theirs from event context (eventData.location,
// isHamburgerMenu, data-toggle), and one widget can map to several types —
// `ws-navigation` is header, footer AND sidebar — so a widget-keyed table
// cannot express them. See the spec's audit section.
const COMPONENT_TYPE_BY_WIDGET = {
	'contact-info': 'dealer-information',
	'image-default': 'feature',
	'links-list': 'feature',
	'map-dynamic': 'dealer-information',
	'model-selector-responsive': 'vehicle-selection',
	'ws-content-cta': 'feature',
	'ws-facet-browse': 'search',
	'ws-hours': 'dealer-information',
	'ws-inv-listing': 'vehicle-inventory',
	'ws-rec-vehicles': 'vehicle-selection',
	'ws-site-text-search': 'search',
	'ws-specials': 'specials',
	'ws-why-buy': 'feature',
	// Not in original spec widget mapping
	'model-selector': 'vehicle-selection',
	'ws-vehicle-ctas': 'vehicle-inventory',
	'ws-mycars-vdp': 'vehicle-inventory',
	'ws-detailed-incentives': 'vehicle-inventory'
};

export function getComponentType({ eventData } = {}) {
	return COMPONENT_TYPE_BY_WIDGET[eventData?.widgetName];
}

// SDG §5.4.1 numeric `location` (US1906349): the 1-based position of the firing
// widget among the VISIBLE widgets of the same component type, in page order.
//
// The firing instance comes from eventData.widgetId, which DDC already supplies
// on ddc.link.generic, ddc.link.navClick and widgetUserEvent — no DOM-element
// sniffing needed. Order is DOM document order, which is scroll-invariant;
// measuring y-positions instead would rank a `position: sticky` header last once
// the page is scrolled. DDC's own pageWidgets list is unusable here: it is
// deduplicated, unordered, and names v9 portlets rather than widgets.
//
// Scanned per event rather than cached at page load. Measured cost is ~19us over
// 40 widget nodes, and a load-time snapshot is provably wrong — on the Stellantis
// homepage ws-site-text-search hydrates INTO visibility, ws-rec-vehicles renders
// 128px tall then collapses to 0, and a ws-content-cta goes hidden after ~2s.
// Each of those moves ordinals, and the last one rules out caching on first
// interaction too.
//
// `type` may be passed by a rule that derives its own type from event context;
// it defaults to the widget lookup. Candidates are still classified through the
// table, so an override for a region type (header/footer/sidebar) finds no
// candidates and yields undefined. Note eventData.location is DDC's region
// string (header/mainNav/footer) and is unrelated to this attribute.
export function getComponentLocation({ eventData, type } = {}) {
	const widgetId = eventData?.widgetId;
	const componentType = type || getComponentType({ eventData });

	if (!componentType || !widgetId) {
		return undefined;
	}

	try {
		const win = window;
		const sameType = Array.from(
			win.document.querySelectorAll('[data-widget-id][data-widget-name]')
		).filter(
			(widget) =>
				COMPONENT_TYPE_BY_WIDGET[
					widget.getAttribute('data-widget-name')
				] === componentType &&
				// isRenderedVisible (Task 1) is a hoisted function declaration
				// defined later in this file; it is shared with the
				// above-the-fold scan and lives next to it.
				// eslint-disable-next-line no-use-before-define
				isRenderedVisible(widget, win)
		);
		const index = sameType.findIndex(
			(widget) => widget.getAttribute('data-widget-id') === widgetId
		);

		return index === -1 ? undefined : index + 1;
	} catch (_error) {
		// DOM unavailable — emit nothing rather than a wrong ordinal
		return undefined;
	}
}

// Build the Adobe "Link Name" (eVar75) component-text slug from a DDC
// link's visible text. Shared by the stellantis CTA / linkClick mappings
// so the pipe-delimited eVar75 taxonomy (dealer|<type>|<location>|<title>|
// <text>) stays consistent and can't be corrupted. Strips trailing
// suffixes that are dynamic or DDC-appended — the "| open in <same|new>
// tab" affordance and a "| <count>" badge (e.g. "Recently Viewed | 4") —
// then lowercases and collapses whitespace + literal pipes to single
// hyphens so an internal pipe (e.g. a creative name like "NAT | 2026 RAM
// ...") cannot inject extra eVar75 delimiters.
export const toAdobeLinkNameSlug = (text = '') =>
	text
		.replace(/\s*\|\s*open in (same|new) tab\s*$/i, '')
		.replace(/\s*\|\s*\d+\s*$/, '')
		.trim()
		.toLowerCase()
		.replace(/[|\s]+/g, '-')
		.replace(/^-+|-+$/g, '');

// The eVar75 <component text> / <component title> slug: lower-case and
// hyphen-delimit whitespace, KEEPING other punctuation — per the SDG examples
// "Sales: 531-352-4611" -> "sales:-531-352-4611" (colon kept). Pipes collapse
// too (TA4137163): eVar75 is itself pipe-delimited, so a literal "|" in a value
// injects extra segments. toAdobeLinkNameSlug above does the same, plus strips
// DDC's "| open in new tab" / "| N" suffixes, so these should consolidate.
export const toSlug = (value = '') =>
	`${value}`
		.toLowerCase()
		.trim()
		.replace(/[|\s]+/g, '-')
		.replace(/^-+|-+$/g, '');

// The <component text> for a social interaction (SDG 4.13 / 5.4.2.7) is the
// NETWORK, per the SDG's eVar75 examples (dealer|footer|1|n/a|facebook), so
// resolve the bare network id in preference order:
//   1. the anchor's social hook attribute — the same attributes the producer
//      binds on, so present whenever the event is, holding 'facebook'.
//      `element` is the clicked node, passed alongside eventData because the
//      engine blanks eventData.element to keep that object serializable.
//   2. the link host: facebook.com/sharer.php -> facebook. Covers the case
//      where `element` does not reach the payload.
//   3. eventData.service, then the link text.
//
// service and text come LAST on purpose: the header icons are SVG-only anchors
// with no text node, so DDC falls back to title="Visit us on Facebook" and both
// arrive as that dealer-authored, localisable prose (verified live 2026-08-27).
//
// Shared by the default mapping and the page-alias overrides so the preference
// order is not forked.
const SOCIAL_NETWORK_ATTRIBUTES = [
	'data-social-header-button-id',
	'data-links-share-link-id',
	'data-ga-socialevent'
];

export const getSocialNetwork = ({ element, eventData = {} } = {}) => {
	const { service, url, text, attributes = {} } = eventData;

	const hook = SOCIAL_NETWORK_ATTRIBUTES.map(
		(attr) => element?.getAttribute?.(attr) || attributes[attr]
	).find(Boolean);
	if (hook) {
		return hook;
	}

	try {
		const { hostname } = new URL(url, window?.location?.origin);
		const [label] = hostname.replace(/^(www|m)\./i, '').split('.');
		if (label) {
			return label;
		}
	} catch (_error) {
		// not a resolvable URL — fall through
	}

	return service || text;
};

// US2046695 / TC1029576 — SDG 5.4.2.5 granular click-to-call attributes.
// Shared by the default mapping's two emit paths (the ddc.link.generic rule and
// the telephonicURL trigger, for phones that also keep a CTA/buttonClick) and by
// the page-alias overrides, so the department resolution is not forked.
//   - type: from the click location (header / footer / overlay), else
//     dealer-information (contact-info), per US1906349.
//   - clickToCallDepartment: from the clean departmentId; contact-info's
//     `department` is a display label, so it is only a fallback.
//   - location: hardcoded 1 (US2061364).
export const getGranularClickToCallAttributes = ({
	location = '',
	widgetHeading = '',
	text = '',
	department = '',
	departmentId = ''
} = {}) => {
	let type = 'dealer-information';
	if (location === 'header') {
		type = 'header';
	} else if (location === 'footer') {
		type = 'footer';
	} else if (location === 'overlay') {
		type = 'overlay';
	}

	const CALL_DEPARTMENTS = ['Sales', 'Service', 'Parts'];
	const rawDept = `${departmentId || department}`.trim();
	const deptTitleCase =
		rawDept.charAt(0).toUpperCase() + rawDept.slice(1).toLowerCase();
	const clickToCallDepartment = CALL_DEPARTMENTS.includes(deptTitleCase)
		? deptTitleCase
		: 'Other';

	return {
		site: 'dealer',
		type,
		location: 1,
		description: toSlug(text),
		title: widgetHeading ? toSlug(widgetHeading) : 'n/a',
		clickToCallDepartment
	};
};

// if on vdp use the first vehicle in vehicles array
// if not vdp but there are vehicles then get the full vehicle object from vehicles array
// otherwise use vehicle
// vehicle is most likely passed in as eventData.vehicle
// vehicles is most likely window.DDC.dataLayer.vehicles
const getVehicle = (vehicle = {}, vehicles = []) => {
	let v = vehicle;

	if (window?.DDC?.dataLayer?.page?.pageInfo?.isVdp) {
		[v] = vehicles;
	} else if (vehicles.length) {
		v = vehicles.find((veh) => veh?.uuid === vehicle?.uuid);
	}

	return v;
};

const isOffersPage = (alias) => {
	return (
		(!alias.includes('PARTS') && alias.includes('SPECIALS')) ||
		alias.includes('COUPON') ||
		alias.includes('INCENTIVES')
	);
};
const isFinancePage = (alias) => {
	return (
		(!isOffersPage(alias) && alias.includes('FINANCE')) ||
		alias.includes('FINANCING')
	);
};
const isTradeInPage = (alias) => {
	return alias.includes('TRADE') && !alias.includes('CONFIRM');
};
const isToolPage = (alias) => {
	return (
		alias.includes('INVENTORY_COMPARE') ||
		alias.includes('CALCULATOR_PAYMENT') ||
		isTradeInPage(alias) ||
		alias.includes('INVENTORY_FINDER')
	);
};
const isServicePage = (alias) => {
	return (
		(!isOffersPage(alias) && alias.includes('SERVICE')) ||
		alias.includes('PARTS') ||
		alias.includes('MOPAR') ||
		alias.includes('XTIME')
	);
};
const isHoursOrDirectionsPage = (alias) => {
	return alias.includes('HOURS') || alias.includes('DIRECTIONS');
};
const isAboutOrStaffPage = (alias) => {
	return (
		alias.includes('ABOUT') ||
		alias.includes('STAFF_LISTING') ||
		alias.includes('EMPLOYMENT_LISTING')
	);
};
const isCorporatePage = (alias) => {
	return (
		alias.includes('PRIVACY') ||
		alias.includes('LEGAL') ||
		alias.includes('SITEMAP')
	);
};

const inventoryTypeMapping = {
	1: 'used',
	2: 'new',
	3: 'cpo',
	NEW: 'new',
	USED: 'used',
	CERTIFIED: 'cpo',
	ALL: 'other',
	DEFAULT: ''
};

const mapInventoryType = (compositeType) =>
	inventoryTypeMapping[
		(typeof compositeType === 'string' && compositeType?.toUpperCase?.()) ||
			compositeType
	] || inventoryTypeMapping.DEFAULT;

function compositeTypeQueryFacet(queryFacets) {
	let val;

	// If present, use the compositeType
	if (queryFacets && queryFacets.compositeType) {
		return queryFacets.compositeType;
	}
	// Not present, attempt to guess from DDC page alias
	const pageAlias = window?.DDC?.dataLayer?.page?.pageInfo?.pageName;
	if (window?.DDC?.dataLayer?.page?.pageInfo?.isVlp) {
		if (pageAlias.includes('_NEW')) {
			val = 'new';
		} else if (pageAlias.includes('_CERTIFIED_USED')) {
			val = 'certified';
		} else if (pageAlias.includes('_USED')) {
			val = 'used';
		} else {
			val = 'all';
		}
	}

	return val;
}

// Per US1981453 AC: our SOLR CCODE is 15 chars; Stellantis expects 16 with a
// leading 'C'. Prepend it only when the value is exactly 15 chars long.
export const normalizeCCode = (c) => (c && c.length === 15 ? `C${c}` : c);

// DE752723: the full 16-char CCODE for a click/interaction tied to ONE specific
// vehicle. Unlike getStellantisVehicleCCcode (page level) this never emits the
// trim-agnostic MYC+XXXXXXX, and unlike a bare vehicles[0] read it never lets a
// generic click inherit an arbitrary trim's CCODE: it only resolves a CCODE when
// the event itself carries a specific vehicle. Some events (e.g. widgetUserEvent
// 'save'/'priceAlert') carry a light vehicle with only uuid/vin and no cCode, so
// getVehicle re-resolves the full record from the data layer by uuid before we
// read the CCODE.
export function getClickVehicleCCcode({
	eventData: { vehicle } = {},
	vehicles = []
} = {}) {
	if (!vehicle || !Object.keys(vehicle).length) {
		return undefined;
	}
	const full = getVehicle(vehicle, vehicles);
	return normalizeCCode(full?.cCode || vehicle?.cCode);
}

export const sending = ({ data, event, eventData, flags, preview }) => {
	const reportForEventViewer = {};
	const reportForNewRelic = {};

	let dl = 'digitalData';
	if (preview) {
		dl = 'previewDigitalData';
	}

	// DE752723: enrich a vehicle-tied click/interaction with the specific
	// vehicle's full 16-char CCODE. Adobe's "Vehicle CCode" data element
	// (eVar147) reads event.message.vehicleCCcode — i.e. these event attributes —
	// FIRST, only falling back to the page-level
	// digitalData.page.pageInfo.vehicleCCcode when it is absent. On a listing
	// filtered to one model+year that page-level value is the trim-agnostic
	// MYC+XXXXXXX, so without this a click on one specific vehicle reports the
	// model-year MYC instead of that vehicle's trim CCODE. Only set when the event
	// carries a resolvable specific vehicle; a generic click leaves it unset and
	// correctly falls back to the page value. Flag-gated to match the page-level
	// rollout (getStellantisVehicleCCcode).
	if (
		isAdobeUpdatesOn({ flags }) &&
		data?.attributes &&
		typeof data.attributes === 'object' &&
		!Array.isArray(data.attributes) &&
		data.attributes.vehicleCCcode == null
	) {
		const vehicleCCcode = getClickVehicleCCcode({
			eventData,
			vehicles: window?.DDC?.dataLayer?.vehicles
		});
		if (vehicleCCcode) {
			// eslint-disable-next-line no-param-reassign
			data.attributes.vehicleCCcode = vehicleCCcode;
		}
	}

	if (event === 'ddc.wstagging.first') {
		window[dl] = {
			...(window[dl] || {}),
			...data.digitalData,
			newEvent: (ev) => {
				const event_ = {
					attributes: {},
					eventAction: null,
					eventName: null,
					timeStamp: new Date(),
					type: null,
					...ev
				};

				// Event array is updated and a window.trigger is fired at this point to alert the browser that a new event
				// has occurred.
				window[dl].events.push(event_);
				if (typeof jQuery !== 'undefined') {
					jQuery(window).trigger({
						...event_,
						// if this is not passed there is a jquery error
						type: event_?.type || DEFAULT_TYPE
					});
				}

				return event_;
			}
		};

		reportForEventViewer.payload = window[dl];
		reportForEventViewer.sender = `window.${dl}`;
		reportForNewRelic.clientEventName = 'page load digitalData';
	} else {
		const {
			eventAction,
			eventName,
			type = DEFAULT_TYPE
		} = window[dl].newEvent(data);

		reportForEventViewer.payload = data;
		reportForEventViewer.sender = `window.${dl}.newEvent`;
		reportForNewRelic.clientEventName = `${type} - ${eventAction} ${eventName}`;
	}

	reportForNewRelic.sender = reportForEventViewer.sender;

	return {
		reportsForEventViewer: [reportForEventViewer],
		reportsForNewRelic: [reportForNewRelic]
	};
};

export function getFilteredSearchType({ eventData: { queryFacets } = {} }) {
	return mapInventoryType(compositeTypeQueryFacet(queryFacets));
}

export function getInventoryStatus({
	eventData: { vehicle = {} } = {},
	vehicles = []
}) {
	return {
		Live: 'On_The_Lot',
		live: 'On_The_Lot',
		'In Transit': 'In_Transit',
		in_transit: 'In_Transit',
		'In Transit At Factory': 'Being_Built',
		in_transit_at_factory: 'Being_Built',
		'On Deposit': 'Sale_Pending',
		on_deposit: 'Sale_Pending'
	}[getVehicle(vehicle, vehicles)?.status];
}

/**
 * Builds the pageName taxonomy required by FCA's specification
 */
export function buildPageName({ eventData, mapping }) {
	const { alias } = eventData;
	let pageName = '';
	let mappedAlias =
		mapping?.pagealias?.pageName || alias.toLowerCase().replace(/_/g, '-');

	// ----- Append the category information to the pageName ------
	if (window?.DDC?.dataLayer?.page?.pageInfo?.isVdp) {
		// Do Year and Model for VDP
		const year = window?.DDC?.dataLayer?.vehicles?.[0]?.modelYear;
		const model = window?.DDC?.dataLayer?.vehicles?.[0]?.model;

		pageName += `${year}:${model}:`;
	} else if (window?.DDC?.dataLayer?.page?.pageInfo?.isVlp) {
		// Do 'search-inventory' for VLP
		if (mappedAlias === 'all-inventory') {
			// all-inventory pages have their own override for the mapped alias based on the query facet
			const compositeType = mapInventoryType(
				compositeTypeQueryFacet(
					window?.DDC?.dataLayer?.page?.queryFacets
				)
			);

			pageName += `search-inventory:${compositeType}-inventory`;
			mappedAlias = '';
		} else {
			pageName += 'search-inventory:';
		}
	} else if (isFinancePage(alias)) {
		// Do 'financing' for any financing related pages
		pageName += 'financing:';
	} else if (isOffersPage(alias)) {
		// Do 'offers' - incentives pages
		pageName += 'offers:';
	} else if (isToolPage(alias)) {
		// Do 'tool' - payment calculator, comparing vehicles
		pageName += 'tool:';
	} else if (isServicePage(alias)) {
		// Do 'services' - schedule appointment and order parts
		pageName += 'services:';
	} else if (
		isHoursOrDirectionsPage(alias) ||
		isAboutOrStaffPage(alias) ||
		alias.includes('CONTACT')
	) {
		// Do 'dealer-information' - contact us, driving directions, maps, about us
		pageName += 'dealer-information:';
	} else if (isCorporatePage(alias)) {
		// Do 'corporate' for Privacy, Sitemap, Legal
		pageName += 'corporate:';
	} else if (alias.includes('SHOWROOM')) {
		// This appends the showroom make to the mappedAlias (ex: showroom:chrysler)
		// Also this relies on FCA having makes attached to body styles in the showroom.
		const showroomPath =
			window?.DDC?.dataLayer?.page?.pageInfo?.pagePath?.toLowerCase();
		const regexp = /(chrysler|dodge|jeep|ram)/g;
		const match = regexp.exec(showroomPath);

		if (match) {
			const theMake = match[0];
			// Append theMake to the Mapped Alias based on regex
			mappedAlias += `:${theMake}`;
		}
	}

	// ---- Append the mapped alias to the pageName (with category) -----
	return pageName + mappedAlias;
}

/**
 * Resolves the page type required by FCA spec
 *
 * @returns {string} the page type for the digitalData
 */
export function buildPageType({ eventData, mapping }) {
	let pageType = 'Other';

	if (
		// I think instead of next lines I could use double !! but since I'm not 100% sure, I'll go with tested code
		window?.DDC?.dataLayer?.page?.trackingTags?.pageType !== null &&
		window?.DDC?.dataLayer?.page?.trackingTags?.pageType !== undefined &&
		window?.DDC?.dataLayer?.page?.trackingTags?.pageType !== ''
	) {
		pageType = window?.DDC?.dataLayer?.page?.trackingTags?.pageType;
	} else if (mapping?.pagealias?.pageType) {
		pageType = mapping?.pagealias?.pageType;
	} else {
		const { alias } = eventData;
		// Add new mappings at the bottom
		if (alias.includes('SHOWROOM')) {
			// Check showroom url for a year
			const pathParts =
				window?.DDC?.dataLayer?.page?.pageInfo?.pagePath?.split('/');
			if (pathParts?.length > 2 && /^[0-9]{4}$/.test(pathParts[2])) {
				pageType = 'Model Showroom';
			} else {
				pageType = 'Showroom';
			}
		} else if (isOffersPage(alias)) {
			if (alias.includes('SERVICE')) {
				pageType = 'Service Specials';
			} else {
				pageType = 'Dealer Specials';
			}
		} else if (isFinancePage(alias)) {
			pageType = 'Finance';
		} else if (isServicePage(alias)) {
			if (alias.includes('SERVICE_LEAD') || alias.includes('XTIME')) {
				pageType = 'Schedule Service';
			} else if (alias.includes('PARTS_LEAD')) {
				pageType = 'Order Parts';
			} else if (alias.includes('PARTS')) {
				pageType = 'Parts';
			} else {
				pageType = 'Service';
			}
		}
	}

	return pageType;
}

export function getVehicleType({
	eventData: { vehicle = {} } = {},
	vehicles = []
} = {}) {
	const { inventoryType, newOrUsed } = getVehicle(vehicle, vehicles) || {};
	return mapInventoryType(
		inventoryType === 'certified' ? inventoryType : newOrUsed
	);
}

export function buildFilteredSearchFilters({
	eventData: { queryFacets = {} } = {}
}) {
	const filters = [];
	Object.keys(queryFacets).forEach((facet) => {
		// Exclude 'compositeType' and 'search' facets:
		// 	compositeType is used as a field in the events
		//	search is used for the typedSearch event and should not be included for filteredSearch
		if (facet !== 'compositeType' && facet !== 'search') {
			if (typeof queryFacets[facet] === 'string') {
				queryFacets[facet].split(',').forEach((v) => {
					filters.push(`${facet}: ${v}`);
				});
			} else {
				filters.push(`${facet}: ${queryFacets[facet]}`);
			}
		}
	});

	return filters.join('|');
}

// Per US1946646: an image counts as AI when the vehicle is In Transit, the
// vehicle has any images, and the image is not a DDC stock photo. DDC stock
// library images live on a Dealer.com image host under a `/ddc/` path root —
// `images.dealer.com/ddc/...`, `pictures.dealer.com/ddc/...`. Stellantis
// explicitly tells us we cannot differentiate AI from non-AI images directly;
// this proxy is the agreed approximation.
const DDC_IMAGE_HOST =
	/(?:^|\/\/)(pictures|images|static)(-np)?\.([a-z0-9-]+\.)*dealer\.com(?=[/:?#]|$)/i;
const DDC_STOCK_IMAGE_PATH = /(?:^|\/\/)[^/]*\/ddc\//i;
const isDDCStockImage = (uri = '') =>
	DDC_IMAGE_HOST.test(uri) && DDC_STOCK_IMAGE_PATH.test(uri);

const IN_TRANSIT_STATUSES = new Set([
	'In Transit',
	'in_transit',
	'In Transit At Factory',
	'in_transit_at_factory'
]);

const isInTransit = (status) => IN_TRANSIT_STATUSES.has(status);

const isAIVehicleImage = (image, vehicle) =>
	isInTransit(vehicle?.status) &&
	vehicle?.images?.length > 0 &&
	!isDDCStockImage(image?.uri);

// Per DE747968: on a VDP, the authoritative list of displayed images is
// published by ws-vehicle-media to `pageInfo.vdpImageUrls` (it scrapes its own
// server store at load). `dataLayer.vehicles[].images` is unreliable here — Solr
// truncates it to a single image — so we prefer the widget-published list when
// present and fall back to vehicle images otherwise (SRP, or before
// ws-vehicle-media has shipped the publisher).
const getRawVdpImageUrls = () => {
	const urls = window?.DDC?.dataLayer?.page?.pageInfo?.vdpImageUrls;
	return Array.isArray(urls) ? urls : [];
};

// Deduped across widget instances — right for COUNTING callers
// (getTotalAIVehicleImageCount, getVDPDefaultVehicleImageAI). Do NOT use this
// for a positional/index read: de-duping shifts every index after the first
// duplicate, so `getGalleryAsset` (which resolves a specific photo by its
// 1-based position) must read the RAW list instead — see getRawVdpImageUrls.
const getVdpImageUrls = () => [...new Set(getRawVdpImageUrls())];

// Per US2046691 (SDG 5.4.2.13.3 / 5.4.2.13.4): eVar172 reports
// `image|<assetSource>|<assetName>`, so assetName is the image file name with
// the resizing query string stripped.
//
// Deliberately does NOT reuse `isAIVehicleImage` here (unlike other callers in
// this file): that helper also gates on `vehicle?.images?.length > 0`. When
// `uri` was sourced from `vehicle.images[index - 1]`, that gate is redundant
// (a non-empty `images` array is implied). But when `uri` was sourced from the
// widget-published `pageInfo.vdpImageUrls` list — a different producer, on
// different timing, than `vehicle.images` — the gate can be false while the
// photo is genuinely AI-generated, silently mis-tagging it as `dealership`.
// `getVDPDefaultVehicleImageAI` (above) already solves this by using the bare
// `isInTransit` + `isDDCStockImage` check on its vdpImageUrls branch; mirror
// that same uniform test for both callers here instead of branching per
// source.
const toAssetDetails = (uri, vehicle) => {
	const [clean] = `${uri || ''}`.split('?');
	const assetName = clean.split('/').pop();

	if (!assetName) {
		return { assetName: 'n/a', assetSource: 'dealership' };
	}

	return {
		assetName,
		assetSource:
			isInTransit(vehicle?.status) && !isDDCStockImage(clean)
				? 'ai-generated'
				: 'dealership'
	};
};

// US2046691: the VDP gallery reports which photo the shopper is on
// (`currentItem`, 1-based) rather than handing us the clicked node, so the asset
// is resolved by index. Prefer the widget-published displayed-image list
// (DE747968 — ws-vehicle-media publishes it); ws-vehicle-gallery publishes no
// vdpImageUrls, so fall back to the data-layer vehicle images.
//
// Reads the RAW (non-deduped) list: `currentItem` is the widget's own 1-based
// position in the list it rendered, so a duplicate URL earlier in that same
// list is still a distinct slide at a distinct index. De-duping first (as
// getVdpImageUrls does for its counting callers) would shift every index
// after the first duplicate and resolve the wrong file.
export function getGalleryAsset({ index, vehicle } = {}) {
	if (!Number.isInteger(index) || index < 1) {
		return { assetName: 'n/a', assetSource: 'dealership' };
	}

	const vdpImageUrls = getRawVdpImageUrls();
	const uri = vdpImageUrls.length
		? vdpImageUrls[index - 1]
		: vehicle?.images?.[index - 1]?.uri;

	return toAssetDetails(uri, vehicle);
}

// US2046691: on an SRP the clicked node IS available (ddc.link.generic passes it
// as a separate payload argument), so read the rendered <img> rather than
// `vehicles[].images` — Solr truncates that list to a single image on an SRP, so
// it does not describe the carousel slide the shopper actually clicked.
export function getClickedImageAsset({ element, vehicle } = {}) {
	const img =
		element?.tagName === 'IMG' ? element : element?.querySelector?.('img');
	const uri = img?.getAttribute?.('src') || vehicle?.images?.[0]?.uri;

	return toAssetDetails(uri, vehicle);
}

export function getTotalAIVehicleImageCount({ vehicles = [] } = {}) {
	const vdpImageUrls = getVdpImageUrls();

	// VDP: count the widget-published displayed images that qualify as AI
	// (vehicle In Transit + not a DDC stock photo).
	if (window?.DDC?.dataLayer?.page?.pageInfo?.isVdp && vdpImageUrls.length) {
		if (!isInTransit(vehicles[0]?.status)) {
			return 0;
		}
		return vdpImageUrls.filter((uri) => !isDDCStockImage(uri)).length;
	}

	// SRP / fallback: per-vehicle images from the data layer.
	return vehicles.reduce(
		(sum, vehicle) =>
			sum +
			(vehicle?.images?.filter((img) => isAIVehicleImage(img, vehicle))
				.length || 0),
		0
	);
}

// Per US1906327: ddc-tracking-helpers counts the visible inventory CTAs
// (`.btn` elements inside every `ws-vehicle-ctas` widget) at page load and
// stashes the total on the data layer. ws-tagging does not scrape the DOM; it
// maps that value straight through. Returns the number the producer set (0 when
// a VDP genuinely renders no CTAs), or undefined when it has not populated it.
export function getTotalInventoryCTACount() {
	return window?.DDC?.dataLayer?.page?.pageInfo?.totalInventoryCTACount;
}

// Stellantis WSP 2.0 §5.3.1.1.15–16 (US1946658; VDP-race fix US2041527).
// Scan the header for a clickable phone / get-directions CTA that is visible in
// the viewport on load. This runs at ddc.wstagging.first — the moment ws-tagging
// builds the payload — so the scan reflects the current DOM. That fixes the VDP
// desktop race where the old ddc-tracking-helpers DOMContentLoaded scan published
// a stale/mistimed value. Results are also written back to the shared data layer
// so other tagging projects can reuse them and digitalData mirrors DDC.dataLayer.
const ABOVE_THE_FOLD_PHONE_SELECTOR = '[data-click-to-call], a[href^="tel:"]';
const ABOVE_THE_FOLD_DIRECTIONS_SELECTOR = '[data-ws-tracking-directions]';

// True when the element is rendered: visible per CSS and occupying a real box.
// Shared by the above-the-fold scan and the component-location scan (US1906349),
// which counts only widgets that actually render — tracking shells measure 0x0,
// and a widget that renders empty would otherwise shift every ordinal after it.
function isRenderedVisible(el, win) {
	if (typeof el.checkVisibility === 'function') {
		if (
			!el.checkVisibility({
				checkOpacity: true,
				checkVisibilityCSS: true
			})
		) {
			return false;
		}
	} else {
		// jsdom fallback (no checkVisibility): own computed style only.
		const style = win.getComputedStyle(el);
		if (
			style &&
			(style.display === 'none' ||
				style.visibility === 'hidden' ||
				style.opacity === '0')
		) {
			return false;
		}
	}

	const rect = el.getBoundingClientRect();

	return rect.width > 0 && rect.height > 0;
}

// True when the element is rendered AND inside the viewport on load.
function isAboveTheFoldVisible(el, win) {
	if (!isRenderedVisible(el, win)) {
		return false;
	}

	const rect = el.getBoundingClientRect();
	const viewportHeight =
		win.innerHeight || win.document.documentElement.clientHeight;
	const viewportWidth =
		win.innerWidth || win.document.documentElement.clientWidth;

	return (
		// vertically within the fold
		rect.bottom > 0 &&
		rect.top < viewportHeight &&
		// horizontally within the viewport — guards off-canvas drawers
		rect.right > 0 &&
		rect.left < viewportWidth
	);
}

// Scan the current DOM for the selector, publish the boolean onto the shared
// data layer (the "special place", produced at ddc.wstagging.first timing so it
// is reusable and correctly timed), and return it. Never throws.
function scanAndPublishAboveTheFold(field, selector) {
	const win = window;
	let value = false;
	try {
		value = Array.from(win.document.querySelectorAll(selector)).some((el) =>
			isAboveTheFoldVisible(el, win)
		);
	} catch (_error) {
		value = false;
	}
	try {
		win.DDC.dataLayer.page.pageInfo = win.DDC.dataLayer.page.pageInfo || {};
		win.DDC.dataLayer.page.pageInfo[field] = value;
	} catch (_error) {
		// data layer not ready — digitalData still receives the scanned value
	}
	return value;
}

export function getAboveTheFoldPhone() {
	return scanAndPublishAboveTheFold(
		'aboveTheFoldPhone',
		ABOVE_THE_FOLD_PHONE_SELECTOR
	);
}

export function getAboveTheFoldDirections() {
	return scanAndPublishAboveTheFold(
		'aboveTheFoldDirections',
		ABOVE_THE_FOLD_DIRECTIONS_SELECTOR
	);
}

export function getVDPDefaultVehicleImageAI({ vehicles = [] } = {}) {
	if (!window?.DDC?.dataLayer?.page?.pageInfo?.isVdp) {
		return false;
	}
	const vehicle = vehicles[0];
	const vdpImageUrls = getVdpImageUrls();

	// Prefer the widget-published default (first displayed) image.
	if (vdpImageUrls.length) {
		return (
			isInTransit(vehicle?.status) && !isDDCStockImage(vdpImageUrls[0])
		);
	}

	// Fallback: vehicle's first data-layer image.
	return isAIVehicleImage(vehicle?.images?.[0], vehicle);
}

// US1981453: the specific-vehicle CCODE for click events
// (ddc.page.vehicleDetailView). Mirrors the single-vehicle [[cCode]] lookup but
// normalizes to the 16-char value the client expects.
export function getVehicleCCcode({
	eventData: { vehicle } = {},
	vehicles = []
} = {}) {
	const veh = vehicle?.cCode ? vehicle : vehicles[0] || vehicle;
	return normalizeCCode(veh?.cCode);
}

// Per US1981453 (spec section 3): the page-level vehicle CCODE (Adobe eVar147).
// A specific vehicle (VDP / single result) emits its full CCODE. A listing
// filtered to one known model+year but no single trim emits the trim-agnostic
// CCODE = 9-char Model Year Code (MYC) + 'XXXXXXX'. Generic/mixed pages, or
// pages with no CCODE available, are left unset (do not fabricate a trim).
export function getStellantisVehicleCCcode({ vehicles = [] } = {}) {
	const cCodes = vehicles
		.map((v) => normalizeCCode(v?.cCode))
		.filter(Boolean);

	// No CCODE available on the page → leave unset.
	if (!cCodes.length) {
		return undefined;
	}

	// Specific vehicle with a known trim → full CCODE.
	if (vehicles.length === 1) {
		return cCodes[0];
	}

	// Listing: only emit a trim-agnostic CCODE when the page is actually filtered
	// to a model (queryFacets.model). This avoids a false positive on an
	// unfiltered page that just happens to show one model. When filtered, every
	// shown vehicle sharing one MYC (first 9 chars = make/model/year/series) means
	// the model+year is known but the trim is not → MYC + XXXXXXX. A model filter
	// spanning multiple years yields differing MYCs → leave unset.
	const queryFacets = window?.DDC?.dataLayer?.page?.queryFacets || {};
	if (!queryFacets.model) {
		return undefined;
	}
	const mycs = new Set(cCodes.map((c) => c.substring(0, 9)));
	return mycs.size === 1 ? `${cCodes[0].substring(0, 9)}XXXXXXX` : undefined;
}

export function getVehicleDataForAttributes({
	eventData: { vehicle },
	vehicles = []
}) {
	const veh = vehicle || vehicles[0];
	let vehicleData = {};

	if (veh && Object.keys(veh).length) {
		vehicleData = {
			inventoryStatus: '[[fn.getInventoryStatus]]',
			cCode: normalizeCCode(veh.cCode),
			make: veh.make,
			model: veh.model,
			status: getVehicleType({
				eventData: {
					vehicle: veh
				},
				vehicles
			}),
			trim: veh.trim,
			vin: veh.vin,
			year: veh.modelYear || veh.year
		};
	}

	return vehicleData;
}
